<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform scale-95 opacity-0"
    enter-to-class="transform scale-100 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform scale-100 opacity-100"
    leave-to-class="transform scale-95 opacity-0"
  >
    <div
      v-if="show"
      :class="alertClasses"
      :role="alertRole"
      :aria-live="ariaLive"
    >
      <!-- Icône -->
      <div :class="iconClasses">
        <component :is="iconComponent" :class="iconSize" />
      </div>
      
      <!-- Contenu -->
      <div class="flex-1 min-w-0">
        <div v-if="title" :class="titleClasses">
          {{ title }}
        </div>
        <div :class="messageClasses">
          <slot>{{ message }}</slot>
        </div>
      </div>
      
      <!-- Bouton fermer -->
      <button
        v-if="dismissible"
        type="button"
        :class="closeButtonClasses"
        aria-label="Fermer"
        @click="handleClose"
      >
        <XIcon :class="iconSize" />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import {
  CheckCircle as CheckCircleIcon,
  AlertTriangle as AlertTriangleIcon,
  AlertCircle as AlertCircleIcon,
  Info as InfoIcon,
  X as XIcon
} from 'lucide-vue-next';

export interface AlertProps {
  type?: 'success' | 'warning' | 'error' | 'info';
  title?: string;
  message?: string;
  dismissible?: boolean;
  autoClose?: boolean;
  autoCloseDelay?: number;
  show?: boolean;
}

export interface AlertEmits {
  (e: 'close'): void;
  (e: 'update:show', value: boolean): void;
}

const props = withDefaults(defineProps<AlertProps>(), {
  type: 'info',
  title: undefined,
  message: undefined,
  dismissible: true,
  autoClose: false,
  autoCloseDelay: 5000,
  show: true,
});

const emit = defineEmits<AlertEmits>();

const internalShow = ref(props.show);

// Synchronisation avec la prop show
watch(() => props.show, (newValue) => {
  internalShow.value = newValue;
});

watch(internalShow, (newValue) => {
  emit('update:show', newValue);
});

// Auto-close
let autoCloseTimer: number | null = null;

const startAutoClose = (): void => {
  if (props.autoClose && props.autoCloseDelay > 0) {
    autoCloseTimer = window.setTimeout(() => {
      handleClose();
    }, props.autoCloseDelay);
  }
};

const clearAutoClose = (): void => {
  if (autoCloseTimer) {
    window.clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
};

onMounted(() => {
  if (internalShow.value) {
    startAutoClose();
  }
});

watch(internalShow, (newValue) => {
  if (newValue) {
    startAutoClose();
  } else {
    clearAutoClose();
  }
});

const handleClose = (): void => {
  internalShow.value = false;
  emit('close');
  clearAutoClose();
};

// Classes computées
const alertClasses = computed((): string => {
  const baseClasses = 'flex items-start gap-3 p-4 rounded-lg border shadow-sm';
  
  const typeClasses = {
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  
  return [baseClasses, typeClasses[props.type]].join(' ');
});

const iconClasses = computed((): string => {
  const typeClasses = {
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
    info: 'text-blue-500',
  };
  
  return ['flex-shrink-0', typeClasses[props.type]].join(' ');
});

const iconComponent = computed(() => {
  const iconMap = {
    success: CheckCircleIcon,
    warning: AlertTriangleIcon,
    error: AlertCircleIcon,
    info: InfoIcon,
  };
  
  return iconMap[props.type];
});

const iconSize = computed((): string => {
  return props.title ? 'w-5 h-5' : 'w-4 h-4';
});

const titleClasses = computed((): string => {
  return 'font-semibold text-sm mb-1';
});

const messageClasses = computed((): string => {
  return props.title ? 'text-sm' : 'text-sm font-medium';
});

const closeButtonClasses = computed((): string => {
  const baseClasses = 'flex-shrink-0 inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors';
  
  const typeClasses = {
    success: 'text-green-500 hover:bg-green-200 focus:ring-green-600',
    warning: 'text-yellow-500 hover:bg-yellow-200 focus:ring-yellow-600',
    error: 'text-red-500 hover:bg-red-200 focus:ring-red-600',
    info: 'text-blue-500 hover:bg-blue-200 focus:ring-blue-600',
  };
  
  return [baseClasses, typeClasses[props.type]].join(' ');
});

const alertRole = computed((): string => {
  return props.type === 'error' ? 'alert' : 'status';
});

const ariaLive = computed((): 'assertive' | 'polite' => {
  return props.type === 'error' ? 'assertive' : 'polite';
});

// Expose show pour v-model
defineExpose({ show: internalShow });
</script> 